import { initClient, tsRestFetchApi, type ApiFetcherArgs } from "@ts-rest/core";
import { contract, isAuthCall } from "@gb/contracts";

// VITE_* vars are inlined at build time by Vite.
export const apiBaseUrl =
  import.meta.env.VITE_API_URL ?? "http://localhost:3000";

/**
 * The current access token, held outside React.
 *
 * `initClient` builds one module-level client, so the token cannot come from
 * context — and threading `headers` through every call site (as
 * `connection-status.tsx` does for `/auth/me`, whose contract declares the
 * header explicitly) would force every future feature contract to declare
 * `authorization` as well. `AuthProvider` owns the value and pushes it here, so
 * there is still exactly one source of truth; this module never touches
 * storage itself.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/**
 * How the client gets a new access token when the one it has is dead.
 *
 * Registered by `AuthProvider`, which owns the refresh token and the storage —
 * this module still never touches either. Resolves to the new access token, or
 * null when the session is genuinely over.
 */
type Refresher = () => Promise<string | null>;

let refresher: Refresher | null = null;

export function setTokenRefresher(fn: Refresher | null): void {
  refresher = fn;
}

/**
 * The single in-flight refresh.
 *
 * Every query on a screen fires at once, so an expired token produces a burst
 * of 401s — five on the project screen alone. Without this they would each
 * start their own refresh, and since refreshing rotates the refresh token, the
 * first to land would invalidate the rest and sign a working session out.
 */
let inFlight: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  if (!refresher) return Promise.resolve(null);
  inFlight ??= refresher().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

const withToken = (
  args: ApiFetcherArgs,
  token: string | null,
): ApiFetcherArgs =>
  token
    ? {
        ...args,
        headers: { ...args.headers, authorization: `Bearer ${token}` },
      }
    : args;

export const api = initClient(contract, {
  baseUrl: apiBaseUrl,
  baseHeaders: {},
  // Attached here rather than through `baseHeaders` so a signed-out client
  // sends no `authorization` header at all: `baseHeaders` values resolve to
  // strings, so the alternative is an empty header, which reads as a malformed
  // credential rather than an absent one.
  api: async (args: ApiFetcherArgs) => {
    const response = await tsRestFetchApi(withToken(args, accessToken));

    // An access token lives 15 minutes; a session lives a week. Without this,
    // every screen breaks a quarter of an hour after sign-in and the only cure
    // is signing out and back in — the refresh token sitting in storage was
    // used for nothing but logout.
    //
    // Retried exactly once, and only for a request that actually carried a
    // token: a 401 with no credentials is not a stale session, it is a
    // signed-out client, and retrying it would loop.
    if (response.status !== 401 || !accessToken || isAuthCall(args)) {
      return response;
    }

    const fresh = await refreshOnce();
    if (!fresh) return response;
    return tsRestFetchApi(withToken(args, fresh));
  },
});

/**
 * A `fetch` with the same credentials, and the same single-flight refresh, as
 * the ts-rest client above.
 *
 * For the routes that are deliberately off the contract because their response
 * is not a typed body: the chat's event stream, and the byte routes that answer
 * 302 to a presigned URL. Neither can go through `initClient`, and for the
 * stream `EventSource` is not an option either — it cannot send an
 * `Authorization` header at all.
 *
 * The point of putting it here rather than in the feature is that there is
 * exactly one token, one refresher and one in-flight guard. A second auth path
 * would work perfectly until the fifteen-minute mark, and then sign the user
 * out of a screen they were in the middle of using.
 */
export async function authorizedFetch(
  path: string,
  init: RequestInit,
): Promise<Response> {
  const url = `${apiBaseUrl}${path}`;
  const send = (token: string | null): Promise<Response> =>
    fetch(url, {
      ...init,
      headers: {
        ...init.headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });

  const response = await send(accessToken);
  // Retried exactly once, and only for a request that actually carried a token —
  // the same rule as the ts-rest path, for the same reason.
  if (response.status !== 401 || !accessToken) return response;

  const fresh = await refreshOnce();
  return fresh ? send(fresh) : response;
}

/**
 * Fetch bytes from one of the API's 302-to-presigned-URL routes.
 *
 * **The reason this exists at all:** `AuthGuard` reads the bearer token from the
 * `Authorization` header and nothing else, and `<img src>`, `<iframe src>` and a
 * plain `<a href>` navigation send no headers. So a byte route used directly as
 * an element source answers 401, and the browser blocks the response outright
 * (`ERR_BLOCKED_BY_ORB`) — a broken image with nothing in the console to say
 * why. Every document preview, extracted figure and journal photograph in the
 * console goes through here for that reason.
 *
 * Built on `authorizedFetch` rather than beside it, which is what the note on
 * that function asks for: one token, one refresher, one in-flight guard. A
 * second auth path here would work perfectly until the fifteen-minute mark and
 * then break every image on the screen.
 *
 * `redirect: "follow"` is safe and load-bearing: the Fetch specification strips
 * `Authorization` on a **cross-origin** redirect, so the token reaches our API
 * and never reaches S3. Sending it on to a third party is precisely what
 * `lib/upload.ts` refuses to do on the way out.
 */
export async function fetchAuthedBlob(
  path: string,
  type?: string,
): Promise<Blob> {
  const response = await authorizedFetch(path, { redirect: "follow" });
  if (!response.ok)
    throw new Error(`Couldn’t load that file (${response.status})`);
  // `blob()` types the result from the response `Content-Type`. That is the
  // object's stored type, and a blob URL is **same-origin with this app** — so
  // whatever it says is what the browser will treat the bytes as, inside our
  // origin, where the session token lives in `localStorage`. The API's
  // inline/attachment allow-list cannot help: `Content-Disposition` is not
  // honoured by `fetch`, so that branch never runs on this path.
  //
  // Callers that know what they are rendering pass it, and we build the blob
  // with the type *we* chose rather than the one the response asserted.
  if (!type) return response.blob();
  return new Blob([await response.arrayBuffer()], { type });
}

/**
 * Resolve one of those same 302 routes to the storage URL it points at, and
 * read none of the bytes.
 *
 * `fetchAuthedBlob` is the right answer for an `<img>` and the wrong one for
 * the document viewer's frame. A blob URL is same-origin with this app, and
 * Chrome will not start its built-in PDF viewer inside an iframe that carries a
 * `sandbox` attribute — **verified 5 Aug 2026** against Chrome 148, with every
 * sandbox token granted, including `allow-scripts allow-same-origin`. So the
 * only frame that renders a PDF is one with no sandbox at all, and the only
 * safe way to have that is for the frame to be genuinely cross-origin. See
 * `AuthedFrame`, which is the whole reason this function exists.
 *
 * `redirect: "follow"` and then `response.url`, which the Fetch specification
 * defines as the URL after redirects. The same specification strips
 * `Authorization` on the cross-origin hop, so the token reaches our API and
 * never reaches storage — the property `fetchAuthedBlob` relies on too.
 *
 * The body is cancelled the moment the headers land. The URL is the whole
 * point of the call, the frame is about to fetch the object itself, and a
 * hundred-page PDF pulled twice is a real cost on site broadband.
 *
 * `cache: "no-store"` is not hygiene, it is the fix for a stall this caused.
 * The frame asks for the same URL a moment after this does, and a cacheable
 * response that is being written when its reader goes away leaves the entry
 * locked: Chrome holds the frame's request until the lock times out.
 * **Verified 5 Aug 2026:** a 3.4 MB preview took ~25 seconds to appear without
 * this, and the frame's own request started 130 ms after this one returned with
 * it. Bypassing the cache entirely means there is no entry to contend for.
 */
export async function resolveAuthedUrl(path: string): Promise<string> {
  const response = await authorizedFetch(path, {
    redirect: "follow",
    cache: "no-store",
  });
  await response.body?.cancel();
  if (!response.ok)
    throw new Error(`Couldn’t load that file (${response.status})`);
  return response.url;
}

// Both moved to `@gb/contracts`. They describe the **transport** — the
// `RequestValidationError` body ts-rest throws, and which routes must not
// trigger a refresh — rather than anything about this app, and both were worked
// out here and never reached the phone. The cost showed up twice over there: a
// schema rejection read "Request failed (400)", and a wrong password on login
// entered the refresh path and cleared the keychain.
//
// Re-exported under the names every screen already imports.
export { errorMessage, isAuthCall } from "@gb/contracts";
